import { useSetToast } from "@app/components/Toast";
import { Chat, TitleChip } from "@app/shared/components/ai/Chat";
import { FlexCol } from "@design-system/Layout/Flex";
import { ResponseStatus, type ScoreValue } from "@generated/client/graphql";
import { type FC } from "react";
import { useParams } from "react-router-dom";
import {
    useQuickAnswerChat,
    useScoreResponse,
    useTitleGenerator,
} from "../../data";
import { useAgentBreadcrumb } from "../../useAgentBreadcrumb";
import { AiMessageBlock } from "./AiMessageBlock";
import { useQuickAnswerChatTranslations } from "./i18n";

export const QuickAnswerAgent: FC = () => {
    const { agent_id } = useParams();
    const { t, keys } = useQuickAnswerChatTranslations();

    const {
        messages,
        input,
        handleInputChange,
        handleSubmit,
        status,
        reload,
        error,
        stop,
        append,
    } = useQuickAnswerChat();

    const { scoreResponse } = useScoreResponse();

    const { setToastSuccess, setToastError } = useSetToast();

    const exampleMessages = [
        t(keys.example_message_chip_iso),
        t(keys.example_message_chip_policy),
        t(keys.example_message_chip_scope2),
        t(keys.example_message_chip_siret),
    ];

    const title = useTitleGenerator(messages, status, 2);
    useAgentBreadcrumb(agent_id ?? "", title);

    const isLoading = status === "submitted";

    const isDisabled = status !== "ready" || !input.length || !!error;

    const showStopButton = status === "streaming" || status === "submitted";

    const handleScoreResponse = (messageId: string) => {
        return (scoreValue: ScoreValue) => {
            scoreResponse(
                { traceId: messageId, scoreValue },
                {
                    onSettled(data) {
                        if (
                            data?.scoreResponse.status ===
                            ResponseStatus.Success
                        ) {
                            setToastSuccess(t(keys.score_response_success));
                            return;
                        }
                        setToastError(t(keys.score_response_error));
                    },
                },
            );
        };
    };

    const handleExampleMessageChipClick = (message: string) => {
        return () => {
            append({
                id: crypto.randomUUID(),
                role: "user",
                content: message,
            });
        };
    };

    return (
        <FlexCol
            w="full"
            alignItems="center"
            className="h-[calc(100%-8px)] bg-white relative"
        >
            <TitleChip
                title={t(keys.title_chip_title)}
                className="absolute top-0 left-0"
            />
            <Chat>
                {!messages.length ? (
                    <Chat.EmptyState title={t(keys.empty_state_title)} />
                ) : (
                    <Chat.Content>
                        <Chat.Date date={new Date()} />
                        {messages.map((message, idx) =>
                            message.role === "assistant" ? (
                                <AiMessageBlock
                                    parts={message.parts}
                                    key={idx}
                                    onRegenerate={
                                        idx === messages.length - 1
                                            ? reload
                                            : undefined
                                    }
                                    onFeedback={
                                        status === "ready" ||
                                        idx !== messages.length - 1
                                            ? handleScoreResponse(message.id)
                                            : undefined
                                    }
                                    status={status}
                                />
                            ) : (
                                <Chat.UserMessage
                                    key={message.id}
                                    scrollToMessage={
                                        idx === messages.length - 1
                                    }
                                >
                                    {message.content}
                                </Chat.UserMessage>
                            ),
                        )}
                        {isLoading && <Chat.LoadingMessage />}
                        {!!error && (
                            <Chat.ErrorMessage>
                                {t(keys.error_message)}
                            </Chat.ErrorMessage>
                        )}
                    </Chat.Content>
                )}
                <Chat.ScrollToBottomButton />
                <Chat.Input
                    onSubmit={handleSubmit}
                    placeholder={t(keys.input_placeholder)}
                    input={input}
                    disabled={isDisabled}
                    onInputChange={handleInputChange}
                    onStop={showStopButton ? stop : undefined}
                />
                {!messages.length && (
                    <Chat.ExampleMessageSection>
                        {exampleMessages.map((message) => (
                            <Chat.ExampleMessageChip
                                key={message}
                                onClick={handleExampleMessageChipClick(message)}
                            >
                                {message}
                            </Chat.ExampleMessageChip>
                        ))}
                    </Chat.ExampleMessageSection>
                )}
            </Chat>
        </FlexCol>
    );
};
